@if (loading()) {
<app-page-loader loadingType="content" />
} @else {
<app-survey-header [survey]="survey"></app-survey-header>

<div class="p-3 pt-0 h-[calc(100%-var(--spacing)*15)] overflow-y-auto">
  <div class="sticky top-0 -mx-3 mb-3 px-3 py-3 z-10 flex items-center gap-3 border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
    <app-breadcrumb class="flex-1" [items]="[{ label: 'Surveys', route: '/surveys' },{ label: 'Business Rules' }]"/>
    @if (canEdit()) {
    <div class="flex items-center gap-2">
      @if (pendingChangesCount > 0) {
      <button type="button" (click)="requestDiscard()"
          class="rounded-lg border border-gray-400 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100">
        Discard
      </button>
      }
      <button type="button" [disabled]="pendingChangesCount === 0" (click)="requestUpdate()"
          class="rounded-lg border border-blue-600 bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-white hover:text-blue-600
          disabled:opacity-50 disabled:bg-white disabled:border-gray-400 disabled:text-gray-600 disabled:hover:text-gray-600 disabled:cursor-not-allowed"
      >
        Update
        @if (pendingChangesCount > 0) {
        <span class="text-xs">({{ pendingChangesCount }} change{{ pendingChangesCount === 1 ? '' : 's' }})</span>
        }
      </button>
    </div>
    }
  </div>

  @if (error()) {
  <p class="rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">{{ error() }}</p>
  }

  <div class="space-y-3">
    @for (rule of rules; track rule.name) {
    <div class="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-white/3"
      [class.border-warning-300]="getRuleChangeStatus(rule) === 'modified'">

      <div class="p-3 flex flex-wrap items-start justify-between gap-3 bg-gray-100 dark:bg-gray-800 border-b border-gray-200">
        <div>
          <p class="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-white">
            {{ rule.name }}
            @if (getRuleChangeStatus(rule) === 'modified') {
            <span class="rounded-full bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-700 dark:bg-warning-500/10 dark:text-warning-400">Unsaved changes</span>
            }
          </p>
          <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Type: <span class="font-medium text-gray-700 dark:text-gray-300">{{ rule.type }}</span>
          </p>
        </div>

        <label class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input type="checkbox" class="h-4 w-4 disabled:cursor-not-allowed"
            [ngModel]="rule.enabled"
            (ngModelChange)="setEnabled(rule, $event)"
            [disabled]="!canEdit()"/>
          Enabled
        </label>
      </div>

      <div class="p-4 divide-y divide-gray-100 dark:divide-gray-800">
        <!-- Field -->
        <div class="py-3 first:pt-0 last:pb-0">
          <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Field</label>
          <select [ngModel]="rule.field" (ngModelChange)="setField(rule, $event)" [disabled]="!canEdit()"
            class="h-9 rounded-lg border bg-transparent px-2.5 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            [class.border-gray-300]="!isUnknownField(rule)" [class.dark:border-gray-700]="!isUnknownField(rule)" [class.dark:text-white]="!isUnknownField(rule)"
            [class.border-error-300]="isUnknownField(rule)" [class.bg-error-50]="isUnknownField(rule)" [class.text-error-700]="isUnknownField(rule)"
            [class.dark:bg-error-500/10]="isUnknownField(rule)" [class.dark:text-error-400]="isUnknownField(rule)"
            [attr.title]="isUnknownField(rule) ? 'Not found in the survey\'s CMD list' : null">
            <option [ngValue]="null">—</option>
            @if (isUnknownField(rule)) {
            <option [ngValue]="rule.field" class="text-error-600">{{ rule.field }} (not found)</option>
            }
            @for (cmd of cmdFieldOptions; track cmd.dbColumn) {
            <option [ngValue]="cmd.dbColumn" class="text-gray-800">{{ cmd.dbColumn }} [{{ cmd.csvPosition }}]</option>
            }
          </select>
        </div>

        <!-- Priority -->
        <div class="py-3 first:pt-0 last:pb-0">
          <label class="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
            Priority
            <app-info-tooltip>Enable this makes the rule run before the DMD being processed</app-info-tooltip>
          </label>
          <label class="relative inline-flex items-center" [class.cursor-pointer]="canEdit()" [class.cursor-not-allowed]="!canEdit()">
            <input type="checkbox" class="peer sr-only"
              [ngModel]="rule.priority === 1"
              (ngModelChange)="setPriority(rule, $event)"
              [disabled]="!canEdit()"/>
            <div class="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-brand-500 peer-disabled:opacity-50 dark:bg-gray-700"></div>
            <div class="absolute left-1 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"></div>
          </label>
        </div>

        <!-- Segment scope -->
        <div class="py-3 first:pt-0 last:pb-0">
          <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Segment scope</label>
          <div class="mb-2 flex items-center gap-4 text-sm text-gray-700 dark:text-gray-300">
            <label class="flex items-center gap-1.5" [class.cursor-pointer]="canEdit()" [class.cursor-not-allowed]="!canEdit()">
              <input type="radio" [name]="'segmentMode-' + rule.name" [checked]="rule.segmentMode === 'accept'"
                (change)="setSegmentMode(rule, 'accept')" [disabled]="!canEdit()"/>
              Accept
            </label>
            <label class="flex items-center gap-1.5" [class.cursor-pointer]="canEdit()" [class.cursor-not-allowed]="!canEdit()">
              <input type="radio" [name]="'segmentMode-' + rule.name" [checked]="rule.segmentMode === 'reject'"
                (change)="setSegmentMode(rule, 'reject')" [disabled]="!canEdit()"/>
              Reject
            </label>
          </div>

          <div class="flex flex-wrap items-center gap-1.5">
            @for (tag of segmentValues(rule); track tag) {
            <span class="flex items-center gap-1 rounded-full border px-3 py-1 text-xs"
              [class.border-gray-300]="!isUnknownSegment(tag)" [class.bg-gray-50]="!isUnknownSegment(tag)" [class.text-gray-700]="!isUnknownSegment(tag)"
              [class.dark:border-gray-700]="!isUnknownSegment(tag)" [class.dark:bg-gray-800]="!isUnknownSegment(tag)" [class.dark:text-gray-300]="!isUnknownSegment(tag)"
              [class.border-error-300]="isUnknownSegment(tag)" [class.bg-error-50]="isUnknownSegment(tag)" [class.text-error-700]="isUnknownSegment(tag)"
              [class.dark:bg-error-500/10]="isUnknownSegment(tag)" [class.dark:text-error-400]="isUnknownSegment(tag)"
              [attr.title]="isUnknownSegment(tag) ? 'Not found in the organization\'s segment list' : null">
              {{ tag }}
              @if (canEdit()) {
              <button type="button" (click)="removeSegmentTag(rule, tag)" class="text-sm ml-0.5 p-0.5 leading-none text-gray-400 hover:text-error-500">&times;</button>
              }
            </span>
            } @empty {
            <span class="text-xs text-gray-400">—</span>
            }

            @if (canEdit() && rule.segmentMode) {
            <button type="button" (click)="openSegmentPicker(rule)"
              class="rounded-full border border-dashed border-gray-300 px-3 py-1 text-xs text-brand-600 hover:border-brand-400 hover:text-brand-700 dark:border-gray-700 dark:text-brand-300 hover:dark:text-brand-400">+ Add</button>
            }
          </div>
        </div>

        <!-- Parameters -->
        <div class="py-3 first:pt-0 last:pb-0">
          <div class="mb-1.5">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Parameters</label>
            @if (canEdit()) {
            <button type="button" (click)="addParameter(rule)" class="ml-4 rounded-full border border-dashed border-gray-300 px-3 py-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:border-brand-400 dark:border-gray-700 dark:text-brand-300 hover:dark:text-brand-400">+ Add</button>
            }
          </div>
          <div class="relative grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div class="hidden sm:block absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gray-300 dark:bg-gray-600"></div>
            @for (entry of rule.paramEntries; track entry) {
            <div class="flex items-center gap-2">
              <input type="text" placeholder="Key" [(ngModel)]="entry.key" (ngModelChange)="onParameterChange(rule)" [disabled]="!canEdit()" class="h-9 w-1/2 rounded-lg border border-gray-300 bg-transparent px-2.5 text-sm dark:border-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"/>
              <input type="text" placeholder="Value" [(ngModel)]="entry.value" (ngModelChange)="onParameterChange(rule)" [disabled]="!canEdit()" class="h-9 w-1/2 rounded-lg border border-gray-300 bg-transparent px-2.5 text-sm dark:border-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"/>
              @if (canEdit()) {
              <button type="button" (click)="removeParameter(rule, entry)" class="shrink-0 rounded-lg p-1.5 text-error-500 hover:bg-error-50 dark:hover:bg-error-500/10">&times;</button>
              }
            </div>
            } @empty {
            <p class="text-xs text-gray-400">No parameters.</p>
            }
          </div>
        </div>

        <!-- Values -->
        <div class="py-3 first:pt-0 last:pb-0">
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Values</label>
          <div class="flex flex-wrap items-center gap-1.5">
            @for (value of rule.values; track $index; let i = $index) {
              @if (rule.editingValueIndex === i) {
              <input type="text" [id]="valueInputId(rule, i)" [ngModel]="value" (ngModelChange)="rule.values[i] = $event"
                (blur)="stopEditingValue(rule)" (keydown.enter)="stopEditingValue(rule)" (keydown.escape)="stopEditingValue(rule)"
                class="h-7 w-32 rounded-full border border-brand-400 bg-white px-3 text-xs text-gray-800 focus:outline-none dark:bg-gray-900 dark:text-white"/>
              } @else {
              <span (click)="startEditingValue(rule, i)"
                class="flex items-center gap-1 rounded-full border border-gray-300 bg-gray-50 px-3 py-1 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                [class.cursor-pointer]="canEdit()" [class.hover:bg-gray-100]="canEdit()" [class.dark:hover:bg-gray-700]="canEdit()">
                {{ value || '(empty)' }}
                @if (canEdit()) {
                <button type="button" (click)="removeValue(rule, i); $event.stopPropagation()" class="text-sm ml-0.5 p-0.5 leading-none text-gray-400 hover:text-error-500">&times;</button>
                }
              </span>
              }
            } @empty {
            <p class="text-xs text-gray-400">No values.</p>
            }
            @if (canEdit()) {
            <button type="button" (click)="addValue(rule)" class="rounded-full border border-dashed border-gray-300 px-3 py-1 text-xs text-brand-600 hover:border-brand-400 hover:text-brand-700 dark:border-gray-700 dark:text-brand-300 hover:dark:text-brand-400">+ Add</button>
            }
          </div>
        </div>
      </div>
    </div>
    } @empty {
    <div class="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center dark:border-gray-700 dark:bg-white/3">
      <p class="text-sm text-gray-500 dark:text-gray-400">No business rules found for this survey.</p>
    </div>
    }
  </div>
</div>
}

@if (openSegmentPickerRule; as pickerRule) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="closeSegmentPicker()">
  <div class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">
      Select segments
      <span class="font-normal capitalize text-gray-400">({{ pickerRule.segmentMode }})</span>
    </h3>
    <div class="mt-4 flex max-h-80 flex-wrap gap-1.5 overflow-y-auto">
      @for (segment of availableSegments; track segment) {
      <button type="button" (click)="toggleSegmentValue(pickerRule, segment)"
        class="rounded-full border px-3 py-1 text-xs font-medium transition-colors"
        [class.bg-brand-500]="isSegmentSelected(pickerRule, segment)"
        [class.border-brand-500]="isSegmentSelected(pickerRule, segment)"
        [class.text-white]="isSegmentSelected(pickerRule, segment)"
        [class.border-gray-300]="!isSegmentSelected(pickerRule, segment)"
        [class.text-gray-700]="!isSegmentSelected(pickerRule, segment)"
        [class.hover:border-brand-400]="!isSegmentSelected(pickerRule, segment)"
        [class.dark:border-gray-700]="!isSegmentSelected(pickerRule, segment)"
        [class.dark:text-gray-300]="!isSegmentSelected(pickerRule, segment)">
        {{ segment }}
      </button>
      } @empty {
      <p class="text-xs text-gray-400">No segments available for this organization.</p>
      }
    </div>
    <div class="mt-5 flex justify-end">
      <button type="button" (click)="closeSegmentPicker()"
        class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Done
      </button>
    </div>
  </div>
</div>
}

@if (showDiscardConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="cancelDiscard()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Discard changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }} will be lost and the business rules will be restored to their last saved state.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelDiscard()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="confirmDiscard()" class="rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white hover:bg-error-700">
        Discard
      </button>
    </div>
  </div>
</div>
}

@if (showUpdateConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
  <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">

    @if (!updateResult() && !updateError()) {
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Update business rules?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {{ pendingChangesCount }} rule{{ pendingChangesCount === 1 ? '' : 's' }} will be updated.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="closeUpdateDialog()" [disabled]="updating()"
        class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="confirmUpdate()" [disabled]="updating()"
        class="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-70">
        @if (updating()) {
        <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
        Updating...
        } @else {
        Confirm
        }
      </button>
    </div>
    } @else {
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Update result</h3>

    @if (updateError()) {
    <p class="mt-3 rounded-lg bg-error-50 px-3 py-2 text-sm text-error-700 dark:bg-error-500/10 dark:text-error-400">{{ updateError() }}</p>
    } @else if (updateResult(); as result) {
      @if (result.isFullSuccess) {
      <p class="mt-3 rounded-lg bg-success-50 px-3 py-2 text-sm text-success-700 dark:bg-success-500/10 dark:text-success-400">
        All {{ result.applied.length }} rule{{ result.applied.length === 1 ? '' : 's' }} updated successfully.
      </p>
      } @else {
      <p class="mt-3 rounded-lg bg-warning-50 px-3 py-2 text-sm text-warning-700 dark:bg-warning-500/10 dark:text-warning-400">
        {{ result.applied.length }} rule{{ result.applied.length === 1 ? '' : 's' }} applied, {{ result.failed.length }} failed.
      </p>
      @if (result.failed.length > 0) {
      <ul class="mt-2 space-y-1 text-xs text-error-600 dark:text-error-400">
        @for (failure of result.failed; track failure.name) {
        <li>{{ failure.name }} ({{ failure.type }}): {{ failure.reason }}</li>
        }
      </ul>
      }
      }
    }

    <div class="mt-5 flex justify-end">
      <button type="button" (click)="closeUpdateDialog()"
        class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Close
      </button>
    </div>
    }

  </div>
</div>
}

@if (showLeaveConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Discard unsaved changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      You have {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }}. If you leave now they will be lost.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelLeave()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Stay
      </button>
      <button type="button" (click)="confirmLeave()" class="rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white hover:bg-error-700">
        Leave & discard
      </button>
    </div>
  </div>
</div>
}
